import { useCallback, useRef, useState } from "react";
import debounce from "lodash-es/debounce";
import { useQuery } from "@apollo/client";
import { ActivityCategory, getActivitiesGQL } from "@onmo/graphql/graphql";
import { PRODUCT_QUERY } from "../constants/achievement";

const LIMIT_ACTIVITIES = 20;

export const useActivities = () => {
  const inFlightRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const prevCountRef = useRef(0);
  const { data, fetchMore, loading } = useQuery(getActivitiesGQL, {
    fetchPolicy: "network-only",
    variables: {
      input: {
        category: ActivityCategory.Profile,
        page: {
          limit: LIMIT_ACTIVITIES,
          offset: 0,
        },
        products: PRODUCT_QUERY,
      },
    },
    onCompleted(res) {
      const items = res?.me?.activities?.items ?? [];
      prevCountRef.current = items.length;
      const total = res?.me?.activities?.total;
      if (typeof total === "number") setHasMore(items.length < total);
    },
  });

  const loadMore = useCallback(async () => {
    if (inFlightRef.current || loading || !hasMore) return;
    inFlightRef.current = true;

    try {
      const currentCount = data?.me?.activities?.items?.length ?? 0;

      const res = await fetchMore({
        variables: {
          input: {
            category: ActivityCategory.Profile,
            page: {
              limit: LIMIT_ACTIVITIES,
              offset: currentCount,
            },
            products: PRODUCT_QUERY,
          },
        },
      });

      const newItems = res?.data?.me?.activities?.items ?? [];
      const afterCount = newItems.length;

      if (afterCount === prevCountRef.current) {
        setHasMore(false);
      } else {
        prevCountRef.current = afterCount;
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [data, fetchMore, hasMore, loading]);
  const loadMoreActivity = debounce(loadMore, 300);

  return {
    activities: data?.me?.activities?.items,
    loadMoreActivity,
    loading,
  };
};
